<script setup>
import { computed } from 'vue'
import { TOPICS, RESOURCE_TYPES, getLabel } from '@/utils/resourceTags.js'
import { formatMonthYear } from '@/utils/formatDate.js'
import { withTrailingSlash } from '@/utils/siteUrl'

const props = defineProps({
  slug:        { type: String, required: true },
  title:       { type: String, required: true },
  description: { type: String, required: true },
  type:        { type: String, required: true },
  topics:      { type: Array,  default: () => [] },
  publishedOn: { type: String, default: '' },
  externalUrl: { type: String, default: '' },
  thumbnail:   { type: String, default: '' },
  // Logo/brand thumbnails (e.g. website cards) sit whole and centred instead
  // of the photo-crop treatment, which would clip a horizontal logo.
  thumbnailContain: { type: Boolean, default: false },
  // Re-enable switch (jk-28) for resources with their own /resources/:slug
  // detail page (markdown body, downloads, authors, citation) that have
  // been soft-retired in favour of an external default — set true to route
  // internally again instead of out to externalUrl.
  goInternal: { type: Boolean, default: false },
  // Position in the grid it's rendered in (jk-30) — drives which of the 4
  // alternating solid brand colours the image panel gets. Ignored when
  // thumbnailContain is set (logo cards keep their light neutral chip).
  colorIndex: { type: Number, default: 0 },
})

const isExternal = computed(() => !!props.externalUrl && !props.goInternal)

// jk-30: cycles through 4 solid brand colours by grid position. Was
// blue-light/green-light (the DS's soft tint variants) paired with solid
// pink/yellow — inconsistent, and the two tints read as washed-out next to
// the two full-strength brand hues. All 4 are now solid brand colours.
const CARD_COLORS = [
  'var(--color-card-blue)',
  'var(--color-card-pink)',
  'var(--color-card-green)',
  'var(--color-card-yellow)',
]
const cardColor = computed(() => (
  props.thumbnailContain ? null : CARD_COLORS[props.colorIndex % CARD_COLORS.length]
))

// jk-30: external resources on the old Autism CRC site get a CTA naming the
// specific portal they land on, instead of the generic "Visit site" — helps
// a visitor know they're leaving SRI before they click. Falls back to the
// generic label for any other external destination (including autismcrc.com.au
// paths outside these two sections, e.g. /our-work/delivery/...).
const ctaLabel = computed(() => {
  if (!isExternal.value) return 'Find out more →'
  if (props.externalUrl.includes('knowledge-centre')) return 'View on Autism CRC Knowledge Centre ↗'
  if (props.externalUrl.includes('best-practice')) return 'View on Best Practice Portal ↗'
  return 'Visit site ↗'
})
</script>

<template>
  <component
    :is="isExternal ? 'a' : 'RouterLink'"
    v-bind="isExternal
      ? { href: externalUrl, target: '_blank', rel: 'noopener noreferrer' }
      : { to: withTrailingSlash(`/resources/${slug}`) }"
    class="resource-card"
    :class="{ 'no-image': !thumbnail }"
    :aria-label="`${title} — ${getLabel(RESOURCE_TYPES, type)}${isExternal ? ', opens in new tab' : ''}`"
  >
    <div class="body">
      <div class="meta">
        <span class="tag type">{{ getLabel(RESOURCE_TYPES, type) }}</span>
        <span v-for="topic in topics.slice(0, 2)" :key="topic" class="tag topic">
          {{ getLabel(TOPICS, topic) }}
        </span>
        <time v-if="publishedOn" class="published" :datetime="publishedOn">Published {{ formatMonthYear(publishedOn) }}</time>
      </div>
      <h3 class="title">{{ title }}</h3>
      <p class="description">{{ description }}</p>
      <span class="cta" aria-hidden="true">
        {{ ctaLabel }}
      </span>
    </div>
    <div
      v-if="thumbnail"
      class="image"
      :class="{ contain: thumbnailContain }"
      :style="cardColor ? { background: cardColor } : {}"
    >
      <!-- Decorative: the visible <h3> title sits beside it, so an alt
           duplicating the title would be redundant for screen readers. -->
      <img :src="thumbnail" alt="" loading="lazy" />
    </div>
  </component>
</template>

<style lang="scss" scoped>
.resource-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 4fr) minmax(0, 2fr);
  min-height: 200px;

  // No thumbnail → `.image` doesn't render at all (see template), so there's
  // no decorative colour chip to reserve a column for. Without this, the
  // grid still held the 2fr track open — a flat empty space where the chip
  // would have been, with nothing in it.
  &.no-image { grid-template-columns: 1fr; }
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border);
  border-radius: $radius-lg;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  text-wrap: pretty;
  @include transition(box-shadow, background, transform);

  &:hover {
    background: var(--color-surface);
    box-shadow: 0 4px 6px rgba($color-ink, 0.16);
    transform: translateY(-2px);

    .cta { background-size: 100% 1px; }
    .image img { transform: translateY(-4px); }
  }
  &:focus-visible {
    @include focus-ring;
    .cta { background-size: 100% 1px; }
  }
  &:active { transform: translateY(1px); }

  // Mobile: stack vertically, image below text. .image is taken out of grid
  // flow below (position:absolute, placed via grid-column/grid-row). A
  // fixed-height row reservation (auto 90px) was tried here so .image's
  // inset:0 had a row to bind against, but it left a big blank gap below
  // the image on real mobile viewports — removed; row 2 sizes implicitly
  // instead (confirmed by live testing, not just a build check).
  @media (max-width: #{$bp-sm}) {
    grid-template-columns: 1fr;

    .image {
      grid-column: 1;
      grid-row: 2;
      border-left: none;
      border-top: 1px solid var(--color-border);
    }

    // No thumbnail → no second row to reserve (this is the fixed 90px
    // that was leaving a bare colour strip below the text with no image
    // in it — this is the actual bug fix, not just the desktop column
    // above).
    &.no-image { grid-template-rows: auto; }
  }

  // Taken out of grid flow (grid-column assigns it column 2 for sizing/
  // position purposes, but position:absolute excludes its content from the
  // grid's auto row-height calculation) so the card's height is driven only
  // by .body's content, not by the image below.
  .image {
    grid-column: 2;
    position: absolute;
    inset: 0;
    overflow: hidden;
    // Fallback only — the real per-card colour is set via inline style
    // (ResourceCard.vue's cardColor), alternating through 4 solid brand
    // colours by grid position (jk-30).
    background: var(--color-card-blue);
    border-left: 1px solid var(--color-border);

    // No object-fit. Equal framing on top/left/right relies on a CSS quirk:
    // vertical padding percentages (top/bottom) resolve against the box's
    // WIDTH, same as horizontal ones — so padding: 15% 15% 0 15% gives
    // genuinely equal pixel gaps on those three sides regardless of the
    // card's height. No padding-bottom: the image (width:100% of the padded
    // box, height:auto) is intrinsically taller than the remaining space, so
    // its lower portion runs past the bottom edge and gets clipped by this
    // element's own overflow:hidden — the visible portion favours the top
    // (report title/logo) over the bottom (body graphic).
    padding: 15% 15% 0 15%;

    img {
      display: block;
      width: 100%;
      height: auto;
      @include transition(transform);
    }

    // Logo/brand thumbnails: show the whole mark on a light chip (logos are
    // dark/colour on transparent) instead of the photo-crop, which would clip
    // a horizontal logo. $color-surface (not the CSS custom prop) keeps the
    // chip light in both themes so any logo stays legible.
    &.contain {
      padding: $space-3;
      background: $color-surface;
      // Flex (not grid) centring: the panel has a definite height, so a flex
      // item's percentage max-height resolves against it. A grid auto-row would
      // instead grow to the image's own height, letting a square logo overflow.
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        // Cap by both axes (not width/height: 100%) so the whole logo fits the
        // chip at any aspect. A square/stacked lockup in a wide panel would
        // otherwise be blown up to a too-tall box and clipped by overflow;
        // capping lets it scale to the shorter side and stay fully visible.
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
    }
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    padding: $space-5;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-1;
  }

  .published {
    margin-left: auto;
    font-size: $font-size-cap;
    color: var(--color-text-muted);
    white-space: nowrap;
  }

  .title {
    margin: 0;
    font-size: $font-size-h4;
    font-weight: 600;
    color: var(--color-ink);
    line-height: 1.25;
    flex: 1;
  }

  .description {
    font-size: $font-size-sm;
    color: var(--color-text);
    line-height: $lh-base;
  }
}
</style>
